import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { sectorList } from '../sectorData';
import ImagePlaceholder from '../components/ImagePlaceholder';

const SECTOR_IMAGE = {
  'nhs': { code: 'IMG-003', ext: 'jpg' },
  'hard-services': { code: 'IMG-004', ext: 'jpg' },
  'construction': { code: 'IMG-005', ext: 'jpg' },
};

const V2Sectors = () => (
  <div data-testid="v2-sectors">
    {/* HERO */}
    <section className="pt-20 pb-16 md:pt-28 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Sectors</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05] max-w-4xl">
          One platform.<br /><span className="text-slate-500">Three sector-shaped demo workspaces.</span>
        </h1>
        <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
          Pick a workspace below and start a free 14-day trial — instantly. Read-only access to the live
          demo plus a private sandbox where you can build up to 3 of your own sites. No card required.
        </p>
        <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed max-w-3xl border-l-2 border-blue-500 pl-5" data-testid="v2-sectors-commercial-line">
          We help organisations <span className="text-slate-900 font-medium">reduce risk, improve delivery,
          and strengthen governance</span> — by combining consultancy with platforms like Videra that turn
          complex operations into controlled, measurable, high-performance environments.
        </p>
      </div>
    </section>

    {/* THE 3 LIVE DEMO WORKSPACES — dark, matching demo.videra.app */}
    <section className="py-20 md:py-28 bg-slate-950" data-testid="v2-sectors-workspaces">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">14-day trial · no card · build up to 3 of your own sites</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white max-w-3xl">
            Portfolio assurance, <span className="text-emerald-400">a 14-day trial.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sectorList.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 ${i === 2 ? 'lg:col-span-2 lg:max-w-[calc(50%-12px)]' : ''}`}
              data-testid={`v2-workspace-${s.slug}`}
            >
              {/* Screenshot header — replaces gradient strip */}
              <div className="relative">
                <ImagePlaceholder
                  code={SECTOR_IMAGE[s.slug].code}
                  ext={SECTOR_IMAGE[s.slug].ext}
                  alt={`${s.workspaceName} dashboard`}
                  aspect="16 / 9"
                  brief={`${s.workspaceName} workspace dashboard screenshot`}
                  spec="1600×900 · PNG · <400KB"
                  rounded="rounded-none"
                  dark
                />
                <div className={`absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/90 pointer-events-none`} />
                <div className="absolute top-0 left-0 right-0 px-6 pt-5 flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1.5 ${s.accentBg} border text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded backdrop-blur-sm`}>
                    {s.tag}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/90 font-semibold backdrop-blur-sm bg-slate-950/30 px-2 py-1 rounded">{s.category}</span>
                </div>
              </div>

              <div className="p-7 md:p-8">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h3 className="text-2xl font-medium text-white">{s.workspaceName}</h3>
                  <span className={`${s.badgePill} border text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded`}>
                    {s.workspaceName === 'Videra Healthcare' ? 'FM HARD SERVICES' : (s.workspaceName === 'NHS Estates Portfolio' ? 'NHS' : 'CONSTRUCTION')}
                  </span>
                </div>

                <p className="text-sm text-slate-400 leading-relaxed mb-6">
                  {s.demoDescription}
                </p>

                <div className="flex items-center gap-2 text-xs text-slate-500 mb-6">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Sign up to enter · 14-day trial · no card required
                </div>

                <a
                  href={s.demoUrl}
                 
                  className={`block w-full bg-gradient-to-r ${s.buttonGradient} text-white text-center px-5 py-3.5 rounded-lg font-medium hover:opacity-90 transition-opacity group`}
                  data-testid={`v2-workspace-trial-${s.slug}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Start free trial
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </a>

                <p className="text-[11px] text-slate-500 text-center mt-3">14 days · no card · build up to 3 of your own sites</p>
                <p className="text-[10px] text-slate-600 text-center mt-1">↗ Opens in a new tab · close it to return here</p>

                <div className="mt-6 pt-6 border-t border-slate-800">
                  <Link
                    to={`/sectors/${s.slug}`}
                    className="inline-flex items-center text-sm font-medium text-slate-300 hover:text-white transition-colors group"
                    data-testid={`v2-workspace-learn-${s.slug}`}
                  >
                    Learn more about {s.badge}
                    <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Need help getting set up? — soft consultancy entry */}
    <section className="py-16 md:py-20 bg-white" data-testid="v2-sectors-consultancy-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">Need help getting set up?</p>
            <h3 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900">
              Our consultancy team configures Videra for your portfolio.
            </h3>
            <p className="mt-3 text-slate-600">
              20+ years and 1,000+ projects of NHS, FM and construction delivery experience. Advisory, embedded or full-programme support.
            </p>
          </div>
          <Link to="/consultancy" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-md font-medium text-sm hover:bg-slate-800 transition-colors group flex-shrink-0">
            See consultancy services <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>

    {/* Video section removed — single intro video lives on home */}
  </div>
);

export default V2Sectors;
